// ── "You have used all the websites your plan allows" ───────────────────────
//
// ── WHY THIS IS A DIALOG AND NOT AN ERROR ───────────────────────────────────
// It used to be an error. Clicking "Add Website" opened the add dialog, the
// customer typed a url, pressed the button, and only THEN did the server say
// "Website limit reached. Your plan allows 1 websites." — a refusal for a
// condition that was knowable before they were invited to type anything.
//
// So the limit is checked when the button is clicked, and this is what the
// customer is shown instead of the form. The server check is untouched: this
// saves typing, it is not the enforcement, and a client-side gate must never be
// the only thing between an account and its limit.
//
// ── ONE MESSAGE, FOUR STATES ────────────────────────────────────────────────
// The four plan states differ in three ways and no more — what the plan is
// called, where there is to upgrade TO, and whether the sites can be named — so
// this is one function over those three inputs rather than four blocks of prose
// to keep in step. Each rule below produces the specified wording for every
// state it covers; none of them special-cases a tier by name.
//
//   free trial   One website only on the free trial
//   Basic plan    One website only on the Basic plan
//   Grow         Three websites only on the Grow plan
//   Optimize     Five websites only on the Optimize plan
//
// (OLD: Grow "Two…", Optimize "Three…" — the allowances before migration 020.)
//
// ── WHY THE PLAN NAME IS MAPPED, NOT PRINTED ────────────────────────────────
// `plan_name` is the DATABASE name, and it is not what the tier is called:
// Optimize is stored as "Starter Plan" and Scale as "Pro Plan". Printing it raw
// produced "3 websites on the Starter Plan plan" — a heading naming a tier this
// product does not sell, and does not offer anywhere the Upgrade button leads.
// getPlanDisplayName is the same mapping the plan cards and the header badge
// use, so all three now say one name.

import { getPlanDisplayName, higherPaidTiers } from './planUtils';
import { websiteLabel } from './websiteUrl';

/**
 * The count as a WORD — "Two websites only on the Grow plan".
 *
 * Only the allowances that exist are spelt out. A tier with some other number
 * falls back to the digit, which reads a little flatter but is never wrong; the
 * alternative is a number-to-words routine carrying twenty spellings that no
 * plan will ever ask for.
 */
// OLD: const NUMBER_WORDS = ['', 'One', 'Two', 'Three'];
// Four and Five added: Grow allows three and Optimize five now.
const NUMBER_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five'];
const numberWord = (n) => NUMBER_WORDS[n] || String(n);

/** "Optimize or Scale" — the last pair joined with "or", the rest with commas. */
const joinWithOr = (names) => {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
};

/**
 * The tiers a paid account can move UP to, by display name.
 *
 * Prefers the LIVE plan rows when they have arrived — anything priced above the
 * current plan, cheapest first — so a new tier or a repricing is picked up
 * without editing this file. Falls back to the static ladder in planUtils when
 * they have not, which is the common case: the rows are fetched when the dialog
 * opens, and the sentence has to be right before they land rather than
 * rewriting itself under the reader.
 *
 * Both paths return display names, so they cannot disagree about what a tier is
 * called.
 */
function upgradeTargets(currentDisplayName, plans) {
  const rows = Array.isArray(plans) ? plans : [];
  const current = rows.find(
    (p) => getPlanDisplayName(p).toLowerCase() === String(currentDisplayName ?? '').toLowerCase(),
  );

  if (current && Number.isFinite(Number(current.price_cents))) {
    const names = rows
      .filter((p) => Number(p.price_cents) > Number(current.price_cents))
      .sort((a, b) => Number(a.price_cents) - Number(b.price_cents))
      .map((p) => getPlanDisplayName(p))
      .filter(Boolean);
    // De-duplicated: two rows resolving to one display name (a renamed tier
    // still carrying its old alias) should not read "Scale or Scale".
    const unique = [...new Set(names)];
    if (unique.length > 0) return unique;
  }

  return higherPaidTiers(currentDisplayName);
}

/**
 * How to free a slot, as segments — the sites are named where naming them helps.
 *
 * One site or two, and we can point at exactly what to remove, which is what
 * makes the instruction actionable. Beyond two the list stops being a sentence
 * and starts being a list, so it goes general instead.
 */
function switchSentence(domains) {
  const names = domains.map(websiteLabel).filter(Boolean);

  const target = names.length === 1
    ? [{ text: names[0], strong: true }]
    : names.length === 2
      ? [
        { text: names[0], strong: true },
        { text: ' or ' },
        { text: names[1], strong: true },
      ]
      // Three or more, or none we could name.
      : [{ text: 'one of your current websites' }];

  return [
    { text: 'To switch sites, remove ' },
    ...target,
    { text: ' and add another. Nothing will be deleted — your keywords and content stay saved.' },
  ];
}

/**
 * Was this refusal the website limit?
 *
 * The client gate reads `website_limit` off the cached session, so a session
 * that predates a downgrade — or predates the field entirely — walks past it and
 * meets the server instead. That refusal used to land in the red alert as the
 * server's own sentence ("Website limit reached. Your plan allows 1 websites."),
 * which is the raw wording this dialog exists to replace. Recognising it lets
 * the same message be shown on both paths.
 *
 * The code is checked first because it is the stable contract; the text match is
 * the fallback for POST /websites, which returns the sentence without one.
 */
export function isWebsiteLimitError(err) {
  const data = err?.response?.data;
  if (data?.code === 'WEBSITE_LIMIT_REACHED') return true;
  return /website limit reached/i.test(String(data?.error ?? ''));
}

/**
 * The website-limit dialog's wording, as RichMessage segments.
 *
 * Shape: [[{ text, strong }, …], …] — one array per paragraph. The same shape
 * the billing confirmations use, so the renderer is shared.
 *
 * @param limit        the plan's website_limit
 * @param planSlug     the current plan's slug, for spotting the free tier
 * @param planName     the current plan's database name, mapped for display
 * @param trialExpired free tier only — has the 7-day trial run out?
 * @param domains      the sites already connected, for naming what to remove
 * @param plans        the live plan rows, for naming upgrade targets; optional
 * @returns {{ title: string, paragraphs: Array, ctaLabel: string }}
 */
export function websiteLimitMessage({
  limit, planSlug = '', planName = null, trialExpired = false, domains = [], plans = null,
} = {}) {
  const n = Number(limit);
  // The allowance, or — when it could not be read — the number of sites held.
  // On the server-refusal path those are the same thing: the server has just
  // said this many is too many, so this many IS the allowance. Better than
  // printing a plan's real limit we do not have, and far better than "NaN".
  const count = Number.isFinite(n) && n > 0 ? n : (domains.length || 1);

  const slug = String(planSlug ?? '').toLowerCase();
  const onFreeTier = !slug || slug === 'free';
  const displayName = getPlanDisplayName({ name: planName, slug: planSlug });

  // ── What the plan is called ─────────────────────────────────────────────
  // The free tier is named for the STATE it is in rather than the plan row,
  // because the row is the same either way and the state is what the reader
  // is being told about.
  const tierLabel = onFreeTier
    ? (trialExpired ? 'Basic plan' : 'free trial')
    : `${displayName || 'current'} plan`;

  const title = `${numberWord(count)} website${count === 1 ? '' : 's'} only on the ${tierLabel}`;

  // ── Where there is to go ────────────────────────────────────────────────
  // A free account is pointed at paid plans in general — it has all three to
  // choose from, and naming them here would duplicate the grid it is about to
  // be shown. A paid account is pointed at the tiers actually above it, since
  // "upgrade" is only useful if it says to what.
  const targets = onFreeTier ? [] : upgradeTargets(displayName, plans);
  const firstLine = onFreeTier
    ? 'If you want to add another website, sign up for a paid plan.'
    : targets.length > 0
      ? `If you want to add another website, upgrade to the ${joinWithOr(targets)} plan.`
      // The top tier is unlimited and never reaches this dialog, so this covers
      // only an unrecognised plan name. Vague, but nothing here is invented.
      : 'If you want to add another website, upgrade your plan.';

  return {
    title,
    paragraphs: [
      [{ text: firstLine }],
      switchSentence(domains),
    ],
    // The free tier has no plan to upgrade FROM, so "Upgrade" would name an
    // action it is not taking. Carried with the copy rather than chosen in the
    // JSX so the button and the sentence above it are read — and tested —
    // together.
    ctaLabel: onFreeTier ? 'Get paid plan' : 'Upgrade',
  };
}
